import { useState, useEffect } from "react";
import { useAuth } from "./context/AuthProvider";
import { Route, Routes, useLocation } from "react-router-dom";
import { SocketProvider } from "./context/SocketContext";
import { ModalProvider } from "./context/ModalContext";
import { LoadingProvider } from "./context/LoadingContext";

import Dashboard from "./pages/Dashboard";
import Partenaires from "./pages/partenaires/Partenaires";
import Produits from "./pages/produits/Produits";
import Prospects from "./pages/prospects/prospection/Prospects";
import ProspectDetailPage from "./pages/prospects/prospection/ProspectDetail";
import Leads from "./pages/prospects/leads/Leads";
import Familles from "./pages/prospects/familles/Familles";
import FamilleDetailPage from "./pages/prospects/familles/DetailPage";
import Calendrier from "./pages/calendrier/Calendrier";
import ToDoList from "./pages/tasks/ToDoList";
import Documentation from "./pages/documents/Documentation";
import Marketing from "./pages/documents/Marketing";
import Legal from "./pages/documents/Legal";
import Client from "./pages/documents/Client";
import Quantalys from "./pages/tools/Quantalys";
import Impots from "./pages/tools/Impots";
import Brevo from "./pages/tools/Brevo";
import Allocation from "./pages/tools/Allocation";

import Societes from "./pages/prospects/familles/Societes";
import DetailSocietes from "./pages/prospects/familles/DetailSocietes";
import Audit from "./pages/conseil/Audit";
import AllocationManage from "./pages/conseil/AllocationManage";
import Support from "./pages/Support";
import Forum from "./pages/forum/Forum";
import DossierDetail from "./pages/forum/DossierDetail";
import Transactions from "./pages/transactions/Transactions";
import TransactionDetail from "./pages/transactions/TransactionDetail";

import NotFound from "./pages/errors/NotFound";
import Unauthorized from "./pages/errors/Unauthorized";
import RolesManager from "./pages/settings/RolesManager";

import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import Loader from "./components/Loader";
import SessionManager from "./components/SessionManager";
import FlexBetween from "./components/FlexBetween";

import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./pages/Login";
import SystemErrorAlert from "./components/ui/SystemErrorAlert";
import { AnimatePresence } from "framer-motion";

export default function App() {
  const { user } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(() =>
    typeof window === "undefined" ? true : window.matchMedia("(min-width: 768px)").matches,
  );
  const [isBackendDown, setIsBackendDown] = useState(false);
  const location = useLocation();
  const isLoginPage = location.pathname === "/login";

  useEffect(() => {
    const handleBackendDown = () => {
      setIsBackendDown(true);
    };
    
    // Si une requête réussit, on retire l'alerte
    const handleBackendUp = () => {
      setIsBackendDown(false);
    };

    const handleCollapseSidebar = () => {
      setIsSidebarOpen(false);
    };

    window.addEventListener('backend-down', handleBackendDown);
    window.addEventListener('backend-up', handleBackendUp);
    window.addEventListener('collapse-sidebar', handleCollapseSidebar);

    return () => {
      window.removeEventListener('backend-down', handleBackendDown);
      window.removeEventListener('backend-up', handleBackendUp);
      window.removeEventListener('collapse-sidebar', handleCollapseSidebar);
    };
  }, []);

  return (
      <ModalProvider>
        <SocketProvider>
          <LoadingProvider>
          <div className="flex h-screen w-full overflow-hidden bg-workspace">
            <SessionManager />
            <FlexBetween
              sidebar={user ? <Sidebar user={user} /> : null}
              isSidebarOpen={isSidebarOpen}
              onSidebarOpenChange={setIsSidebarOpen}
            >
              {/* Contenu principal + Navbar */}
              <div className="flex min-w-0 flex-1 flex-col bg-workspace">

                {/*  Navbar dynamique */}
                {user && (
                  <Navbar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
                )}

                {/*  Zone de contenu (Routes) avec Scroll Indépendant */}
                <main
                  className={`flex-1 min-h-0 w-full overflow-y-auto relative ${isLoginPage ? "p-0" : "p-2 md:p-4"
                    }`}
                >
                  <Loader />
                  <Routes>
                      <Route path="/login" element={<Login />} />
                      <Route path="/" element={<ProtectedRoute element={<Dashboard />} />} />
                      <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
                      <Route path="/partenaires" element={<ProtectedRoute element={<Partenaires />} />} />
                      <Route path="/produits" element={<ProtectedRoute element={<Produits />} />} />
                      <Route path="/prospects/leads" element={<ProtectedRoute element={<Leads />} />} />
                      <Route path="/prospects/prospects" element={<ProtectedRoute element={<Prospects />} />} />
                      <Route path="/prospects/:id" element={<ProspectDetailPage />} />
                      <Route path="/prospects/familles" element={<ProtectedRoute element={<Familles />} />} />
                      <Route path="/prospects/familles/:id" element={<ProtectedRoute element={<FamilleDetailPage />} />} />
                      <Route path="/prospects/societes" element={<ProtectedRoute element={<Societes />} />} />
                      <Route path="/prospects/societes/:id" element={<ProtectedRoute element={<DetailSocietes />} />} />
                      <Route path="/conseil/audits" element={<ProtectedRoute element={<Audit />} />} />
                      <Route path="/conseil/allocations" element={<ProtectedRoute element={<AllocationManage />} />} />
                      <Route path="/calendrier" element={<ProtectedRoute element={<Calendrier />} />} />
                      <Route path="/tasks" element={<ProtectedRoute element={<ToDoList />} />} />
                      <Route path="/documents" element={<ProtectedRoute element={<Documentation />} />} />
                      <Route path="/documents/marketing" element={<ProtectedRoute element={<Marketing />} />} />
                      <Route path="/documents/legal" element={<ProtectedRoute element={<Legal />} />} />
                      <Route path="/documents/client" element={<ProtectedRoute element={<Client />} />} />
                      <Route path="/outils/quantalys" element={<ProtectedRoute element={<Quantalys />} />} />
                      <Route path="/outils/impots" element={<ProtectedRoute element={<Impots />} />} />
                      <Route path="/outils/brevo" element={<ProtectedRoute element={<Brevo />} />} />
                      <Route path="/outils/allocation" element={<ProtectedRoute element={<Allocation />} />} />

                      <Route path="/support" element={<ProtectedRoute element={<Support />} />} />
                      <Route path="/forum" element={<ProtectedRoute element={<Forum />} />} />
                      <Route path="/forum/:id" element={<ProtectedRoute element={<DossierDetail />} />} />
                      <Route path="/transactions" element={<ProtectedRoute element={<Transactions />} />} />
                      <Route path="/transactions/:id" element={<ProtectedRoute element={<TransactionDetail />} />} />
                      
                      <Route path="/settings/roles" element={<ProtectedRoute element={<RolesManager />} />} />
                      
                      {/* Pages d'erreurs */}
                      <Route path="/unauthorized" element={<Unauthorized />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                </main>
              </div>
            </FlexBetween>

            {/* Global Backend Down Alert */}
            <AnimatePresence>
              {isBackendDown && !isLoginPage && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                  <div className="w-full max-w-xl">
                    <SystemErrorAlert />
                  </div>
                </div>
              )}
            </AnimatePresence>

          </div>
          </LoadingProvider>
        </SocketProvider>
      </ModalProvider>
  );
}
